import { type ApiClient, isRetryable } from "@luckymenu/api-client";
import type { createQueryKeys } from "@luckymenu/api-client/react";
import type { Kot, Order } from "@luckymenu/core";
import type { RealtimeClient } from "@luckymenu/realtime-client";
import type { QueryClient } from "@tanstack/react-query";
import type { StaffPlatform } from "../adapters";
import { withKot } from "./kots";
import { Outbox } from "./outbox";
import { orderingProblemOf, takenByOrderOf } from "./problems";

/**
 * The staff app's outbox (ordering/outbox.ts), wired to the API, the query
 * cache and the platform: kept in `storage`, holding back while `network`
 * says the device is offline, and trying again at once when the device
 * comes back online or the socket reconnects.
 */
export function createStaffOutbox(options: {
	platform: StaffPlatform;
	client: ApiClient;
	queryClient: QueryClient;
	keys: ReturnType<typeof createQueryKeys>;
	realtime: RealtimeClient | null;
	activeRestaurant: () => string | null;
}): Outbox {
	const { platform, client, queryClient, keys, realtime } = options;
	const ordering = client.staff.ordering;
	const outbox = new Outbox({
		async send(entry) {
			switch (entry.kind) {
				case "createOrder":
					return ordering.createOrder.mutate(entry.input);
				case "addBatch":
					return (await ordering.addBatch.mutate(entry.input)).order;
				case "cancelItem":
					return ordering.cancelItem.mutate(entry.input);
				case "setKotStatus":
					return ordering.setKotStatus.mutate(entry.input);
			}
		},
		failure: (error) => ({
			problem: orderingProblemOf(error),
			retryable: isRetryable(error),
			takenBy: takenByOrderOf(error),
		}),
		activeRestaurant: options.activeRestaurant,
		...(platform.network ? { online: () => platform.network?.isOnline() ?? true } : {}),
		storage: platform.storage,
	});

	// The server's answer goes straight into the cache, before the refetch.
	outbox.onSent((entry, result) => {
		if (entry.kind === "setKotStatus") {
			const kot = result as Kot;
			queryClient.setQueriesData<Kot[]>(
				{ queryKey: keys.staff.ordering.listKots.pathKey() },
				(old) => (old ? withKot(old, kot) : old),
			);
			queryClient.setQueryData<Order>(
				keys.staff.ordering.getOrder.queryKey({ orderId: kot.orderId }),
				(old) => (old ? { ...old, kots: withKot(old.kots, kot) } : old),
			);
		} else {
			const order = result as Order;
			queryClient.setQueryData(keys.staff.ordering.getOrder.queryKey({ orderId: order.id }), order);
		}
		void queryClient.invalidateQueries({ queryKey: keys.staff.ordering.pathKey() });
	});

	platform.network?.subscribe(() => {
		if (platform.network?.isOnline()) outbox.wake();
	});
	// A socket that opens again means the API answers again.
	let wasOpen = false;
	realtime?.subscribe(() => {
		const open = realtime.getStatus().state === "open";
		if (open && !wasOpen) outbox.wake();
		wasOpen = open;
	});
	void outbox.load();
	return outbox;
}
